import { useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';

import { Button } from '@llura/ui';

import type { CoachGender, CoachTone } from '../../stores/coach.store';
import { getCoachSnippet } from '../../lib/coach-audio';
import { useHaptic } from '../../hooks/useHaptic';
import { useStrings } from '../../hooks/useStrings';

interface CoachVoicePreviewProps {
  gender: CoachGender;
  tone: CoachTone;
  /** Bubble up the play state so the parent can pulse the avatar. */
  onPlayingChange?: (playing: boolean) => void;
}

/**
 * Sits under the coach avatar in the reveal step. One-tap preview of the
 * opener snippet, with graceful fallback when the underlying mock audio
 * isn't on disk yet (the placeholder generator script hasn't been run).
 */
export function CoachVoicePreview({ gender, tone, onPlayingChange }: CoachVoicePreviewProps) {
  const { strings } = useStrings();
  const haptic = useHaptic();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);

  const snippet = getCoachSnippet(gender, tone, 'opener');

  useEffect(() => {
    onPlayingChange?.(playing);
  }, [playing, onPlayingChange]);

  const toggle = () => {
    haptic.tap();
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      return;
    }
    el.currentTime = 0;
    el.play().catch(() => {
      // Most often: the mock MP3 isn't present yet. Fail quietly so the UI
      // stays usable — the surrounding flow doesn't depend on real audio.
      setPlaying(false);
    });
  };

  return (
    <div className="flex flex-col items-center gap-1.5">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={toggle}
        aria-label={playing ? strings.coach.voicePreview.pause : strings.coach.voicePreview.play}
        className="gap-1.5 rounded-full border border-secondary/40 bg-secondary/10 px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-foreground hover:bg-secondary/20"
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 translate-x-px" />}
        {playing ? strings.coach.voicePreview.pause : strings.coach.voicePreview.play}
      </Button>
      <audio
        ref={audioRef}
        src={snippet.url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onError={() => setPlaying(false)}
      />
    </div>
  );
}
